import { TextContentElement } from '@deltek/specification-client/core/Models/textContentElement'
import GridItem from '@deltek/specification-client/core/Surfaces/GridItem'
import { BestPracticesIcons } from '@deltek/specification-client/core/Widgets/BestPracticesSetting/BestPracticesIcons'
import Typography from '@material-ui/core/Typography'
import { MoreVert } from '@material-ui/icons'
import { SuggestionData } from 'ckeditor5-premium-features'
import clsx from 'clsx'
import { processEnv } from 'components/configuration/environmentVariables'
import { BestPracticesSettingPopover } from 'components/WorkspaceEditor/components/TreeView/BestPracticesSettingPopover'
import {
    useBestPracticesData,
    useBestPracticesMenu,
} from 'components/WorkspaceEditor/features/bestPractices/hooks'
import { useOptionGroupStyles } from 'components/WorkspaceEditor/styles/useOptionGroupStyles'
import { tryGetShortcutKey } from 'components/WorkspaceEditor/utils/shortcutKeyUtils'
import {
    useProjectSettings,
    useSelectedProjectElementNode,
} from 'components/WorkspaceEditor/Workspace2Store'
import React, { useCallback, useMemo } from 'react'
import { extractSuggestions } from '../ContentEditor/contentUtils'
import { removeUnitOfMeasureDelimiters } from './hooks/useFormatReplacementOption'

/**
 * Determines the base pill class name based on content item state and properties.
 */
const getPillBaseClassName = (
    contentItem: Partial<TextContentElement>,
    isInactive: boolean,
    classes: Record<string, string>
) => {
    if (contentItem.optionItemState === 'OptedOut') {
        return classes.optedOutOptionItem
    }

    if (isInactive || contentItem.optionItemState === 'InActive') {
        return classes.inactivatedOptionItem
    }

    if (contentItem.optionItemState === 'OptedIn') {
        return contentItem.isPremiumPlus
            ? classes.moSelectedOption
            : classes.selectedOptionItem
    }

    return contentItem.isPremiumPlus
        ? classes.moUnselectedOption
        : classes.unselectedOptionItem
}

/**
 * Handles string-type provided classes with appropriate modifiers.
 */
const handleStringClasses = (
    baseClasses: string,
    classNames: Record<string, string>,
    isDisabled?: boolean,
    editable?: boolean
) => {
    const modifiers = []

    if (isDisabled) {
        modifiers.push(classNames.disabledOptionGroup)
    }

    if (editable === false) {
        modifiers.push(classNames.defaultCursor)
    }

    return modifiers.length > 0
        ? `${baseClasses} ${modifiers.join(' ')}`
        : baseClasses
}

/**
 * Determines the appropriate pill style class based on contentItem state.
 * Simplified with helper functions for better maintainability.
 */
const getPillStyle = (
    contentItem: Partial<TextContentElement>,
    isInactive: boolean,
    classNames: Record<string, string>,
    editable?: boolean,
    providedClasses?: Record<string, string>,
    isDisabled?: boolean,
    explicitlyDisabled?: boolean
) => {
    // Handle string-type provided classes
    if (typeof providedClasses === 'string') {
        return handleStringClasses(
            providedClasses,
            classNames,
            isDisabled,
            editable
        )
    }

    // Use provided classes object or fallback to computed classNames
    const classes = providedClasses || classNames
    const baseClassName = getPillBaseClassName(contentItem, isInactive, classes)
    const baseClasses = `${classes.optionItem} ${baseClassName}`

    // Apply state-based modifiers in priority order
    // If explicitly disabled (not just inactive), use disabledPill style
    if (explicitlyDisabled && !isInactive) {
        return `${baseClasses} ${classes.disabledPill}`
    }

    if (isDisabled) {
        return `${baseClasses} ${classes.disabledOptionGroup}`
    }

    if (editable === false) {
        return `${baseClasses} ${classes.defaultCursor}`
    }

    return baseClasses
}

/**
 * Determines the appropriate pill text based on contentItem, unit of measure, and label.
 */
const getPillText = (
    contentItem: Partial<TextContentElement>,
    unitOfMeasure: string,
    label?: string
) => {
    const el = contentItem.elements || []
    const uom = unitOfMeasure.toUpperCase()

    if (contentItem.type === 'SelectionOption' && el && el.length !== 0) {
        const results = el?.reduce((acc, item) => {
            if (item.type === 'Text' && item.text) {
                acc.push(item.text)
            } else if (item.type === 'UnitsOfMeasure') {
                if (uom === 'BOTH' && item.imperialUnits && item.metricUnits) {
                    acc.push(`${item.imperialUnits} (${item.metricUnits})`)
                } else if (uom === 'IMPERIAL' && item.imperialUnits) {
                    acc.push(item.imperialUnits)
                } else if (uom === 'METRIC' && item.metricUnits) {
                    acc.push(item.metricUnits)
                }
            }
            return acc
        }, [] as string[])
        return results.join('')
    }

    return label || removeUnitOfMeasureDelimiters(contentItem.text) || ''
}

/** Props for the SelectionOption component. */
export interface SelectionOptionProps {
    /** The content item to render */
    contentItem: Partial<TextContentElement>
    /** The label to display */
    label?: string
    /** The classes to apply */
    classes?: any
    /** Whether the component is inactive */
    inactive?: boolean
    /** Whether the component is disabled (not hidden, just styled as disabled) */
    disabled?: boolean
    /** Whether the component is editable */
    editable?: boolean
    /** Optional key for the component */
    selector?: string
    /** Whether to reserve space for EmptyPlainTextContainer */
    hasEmptyContainer?: boolean
    /** function to select/deselect pill */
    onSelectionOptionClick?: (
        currentSelectedItem: Partial<TextContentElement>,
        currentlySelected: boolean,
        currentContentItem: Partial<TextContentElement>,
        multipleSelectionType?: string
    ) => void
    /** The parent content item ID (OptionGroup/ReplacementOption container) */
    parentContentItemId?: string
    /** Whether BP editing is disabled */
    isBestPracticeEditDisabled?: boolean
    /** Whether BP viewing is disabled */
    isBestPracticeViewDisabled?: boolean
    disableCursorOnly?: boolean
}

export const SelectionOption = React.memo<SelectionOptionProps>(
    ({
        contentItem,
        label,
        classes,
        inactive = false,
        disabled = false,
        editable = false,
        selector,
        hasEmptyContainer = false,
        onSelectionOptionClick,
        parentContentItemId,
        isBestPracticeEditDisabled = false,
        isBestPracticeViewDisabled = false,
        disableCursorOnly = false,
    }) => {
        const classNames = useOptionGroupStyles()

        const projectSettings = useProjectSettings()
        const selectedProjectElementNode = useSelectedProjectElementNode()

        // Get projectId from selectedProjectElementNode (more reliable than useParams)
        const projectId = selectedProjectElementNode?.projectId

        // Extract Best Practices data using optimized hook
        const { isBestPractice, isGlobalBestPractice } = useBestPracticesData(
            contentItem
        )

        const unitOfMeasure = useMemo(
            () => projectSettings?.unitOfMeasure.toString() ?? 'IMPERIAL',
            [projectSettings]
        )

        // Best Practices state management
        const [mouseOverElement, setMouseOverElement] = React.useState<
            string | null
        >(null)
        const {
            isBPMenuOpen,
            bpAnchorElement,
            openBPMenu,
            closeBPMenu,
        } = useBestPracticesMenu()
        /**
         * Determine if the pill should be disabled (non-interactive)
         *
         * A pill is disabled when any of these conditions are true:
         * - Parent paragraph is inactive (inactive=true)
         * - Component is not editable (editable=false)
         * - Component is explicitly disabled (disabled=true) - used for product groups not added to project
         */
        const isDisabled = inactive || !editable || disabled

        const pillStyle = useMemo(() => {
            const baseStyle = getPillStyle(
                contentItem,
                inactive,
                classNames,
                editable,
                classes,
                isDisabled,
                disabled
            )
            // Apply maxWidth constraint when EmptyPlainTextContainer exists
            // to ensure space for clickable area between pills
            return hasEmptyContainer
                ? `${baseStyle} ${classNames.optionItemWithEmptyContainer}`
                : baseStyle
        }, [
            contentItem,
            inactive,
            classNames,
            editable,
            classes,
            isDisabled,
            disabled,
            hasEmptyContainer,
        ])

        const showBestPracticesFeatureInNewWorkspace: boolean = useMemo(
            () =>
                processEnv.REACT_APP_ShowBestPracticesFeatureInNewWorkspace ===
                '1',
            [processEnv]
        )
        const initialSuggestions: SuggestionData[] = useMemo(
            () => extractSuggestions([contentItem]),
            [contentItem]
        )

        const pillText = useMemo(
            () => getPillText(contentItem, unitOfMeasure, label),
            [contentItem, unitOfMeasure, label]
        )

        const isOptedIn = useMemo(
            () => contentItem.optionItemState === 'OptedIn',
            [contentItem.optionItemState]
        )

        const handleClick = useCallback(
            (event: React.MouseEvent<HTMLDivElement>) => {
                if (isDisabled) return

                const el = event.target as HTMLElement
                const isMoreMenu =
                    el.getAttribute('data-testid') === 'moreMenu' ||
                    el.parentElement?.getAttribute('data-testid') === 'moreMenu'

                if (isMoreMenu) return

                onSelectionOptionClick?.(
                    contentItem,
                    contentItem?.optionItemState === 'OptedIn',
                    contentItem
                )
            },
            [contentItem, onSelectionOptionClick, isDisabled]
        )

        const handleKeyDown = useCallback(
            (e: React.KeyboardEvent) => {
                if (isDisabled) {
                    return
                }

                const enterKey = tryGetShortcutKey('Enter', e, true)
                const spaceKey = tryGetShortcutKey('Space', e, true)

                if (enterKey || spaceKey) {
                    e.preventDefault()
                    handleClick(e as any)
                }
            },
            [handleClick, isDisabled]
        )

        const handleRightClick = useCallback(
            (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                event.preventDefault()
                event.stopPropagation()

                // Check if all required conditions are met to open BP menu
                const canOpenMenu =
                    showBestPracticesFeatureInNewWorkspace &&
                    !isBestPracticeEditDisabled &&
                    !isDisabled &&
                    isOptedIn &&
                    contentItem.id &&
                    parentContentItemId &&
                    projectId

                if (!canOpenMenu) return

                // openBPMenu handles all state updates and data fetching internally
                openBPMenu({
                    anchorElement: event.currentTarget,
                    contentItemId: parentContentItemId,
                    contentItemType: contentItem.type || '',
                    projectId: projectId,
                    // For pills (SelectionOption), pass the contentItem.id as elementId
                    elementId: contentItem.id,
                })
            },
            [
                isDisabled,
                contentItem.id,
                contentItem.type,
                parentContentItemId,
                isBestPracticeEditDisabled,
                isOptedIn,
                projectId,
                openBPMenu,
                showBestPracticesFeatureInNewWorkspace,
            ]
        )

        const handleMouseEnter = useCallback(() => {
            if (isBPMenuOpen || !isOptedIn) return
            setMouseOverElement(contentItem.id!)
        }, [isBPMenuOpen, contentItem.id, isOptedIn])

        const handleMouseLeave = useCallback(() => {
            if (isBPMenuOpen || !isOptedIn) return
            setMouseOverElement(null)
        }, [isBPMenuOpen, isOptedIn])

        const handleBPMenuDisplay = useCallback(
            (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
                if (!projectId || !parentContentItemId) return

                // openBPMenu handles all state updates and data fetching internally
                // It also checks global ref and prevents opening if menu already exists
                openBPMenu({
                    anchorElement: event.currentTarget,
                    contentItemId: parentContentItemId,
                    contentItemType: contentItem.type || '',
                    projectId: projectId,
                    // For pills (SelectionOption), pass the contentItem.id as elementId
                    elementId: contentItem.id,
                })
            },
            [
                contentItem.id,
                contentItem.type,
                parentContentItemId,
                projectId,
                openBPMenu,
            ]
        )

        const handleCloseBPMenu = useCallback(() => {
            // closeBPMenu handles all cleanup internally including setSelectedElementId(undefined)
            closeBPMenu()
            setMouseOverElement(null)
        }, [closeBPMenu])
        return (
            <>
                <Typography
                    key={
                        selector || `option-text-${contentItem.id || 'default'}`
                    }
                    data-testid={`option-text-${contentItem.id || 'default'}`}
                    className={clsx('optionItem', pillStyle)}
                    style={{
                        whiteSpace: 'nowrap !important',
                        display: 'unset !important',
                        cursor: disableCursorOnly ? 'default' : undefined,
                    }}
                    data-suggestion={`content-suggestion-id-${initialSuggestions?.[0]?.id}`}
                    onClick={isDisabled ? undefined : handleClick}
                    onContextMenu={handleRightClick}
                    onMouseEnter={handleMouseEnter}
                    onMouseLeave={handleMouseLeave}
                    onKeyDown={isDisabled ? undefined : handleKeyDown}
                    role={isDisabled ? undefined : 'button'}
                    tabIndex={isDisabled ? -1 : 0}
                    aria-disabled={isDisabled}
                >
                    <span>{pillText}</span>
                    {isOptedIn && (
                        <span className={classNames.endIcon}>
                            {showBestPracticesFeatureInNewWorkspace &&
                                !isBestPracticeViewDisabled &&
                                (isBestPractice || isGlobalBestPractice) && (
                                    <GridItem
                                        className={clsx(
                                            'bpIconContainer',
                                            classNames.activeBPIcon
                                        )}
                                    >
                                        <BestPracticesIcons
                                            isBestPractice={isBestPractice}
                                            isGlobalBestPractice={
                                                isGlobalBestPractice
                                            }
                                        />
                                    </GridItem>
                                )}
                            {showBestPracticesFeatureInNewWorkspace &&
                                !isBestPracticeEditDisabled &&
                                mouseOverElement === contentItem.id && (
                                    <GridItem
                                        onClick={handleBPMenuDisplay}
                                        className={classNames.bpOptContainer}
                                    >
                                        <MoreVert
                                            fontSize="inherit"
                                            data-testid="moreMenu"
                                            style={{ position: 'absolute' }}
                                        />
                                    </GridItem>
                                )}
                        </span>
                    )}
                </Typography>
                {isBPMenuOpen && bpAnchorElement && (
                    <BestPracticesSettingPopover
                        anchorElement={bpAnchorElement}
                        isBestPractice={isBestPractice}
                        isGlobalBestPractice={isGlobalBestPractice}
                        isBestPracticeEditDisabled={isBestPracticeEditDisabled}
                        onCloseMainMenu={handleCloseBPMenu}
                    />
                )}
            </>
        )
    }
)
